/**
 * The header Timer til lønn and Lønnsprosent share (manager redesign, 2026-10-09): one title, one
 * line, the active report's own export as the primary action, and the two reports as route tabs.
 * Lønnsprosent is offered where /omsetning is: the cost capability and the scheduling module, and a
 * cost-only reader sees it without the payroll tab. The Personalliste, its code list and the §10-7
 * print open from «Arbeidstid» here (#173, owner 2026-10-19), for whoever may read the hours.
 */
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Menu } from '@/ui';
import { Icon } from '@/ui/icons';
import { PageHead, type TabItem, Tabs } from '@/ui/page';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import './lonn.css';

/** the work-time views kept by law; Kontroll offers them too to someone who may not open Rapporter */
export const worktimeViews = (go: (to: string) => void) => [
  { key: 'pl', label: T('Personalliste'), onSelect: () => go('/arbeidstid?vis=personalliste') },
  {
    key: 'koder',
    label: T('Kodeliste til personallisten'),
    onSelect: () => go('/arbeidstid?vis=personalliste&liste=koder'),
  },
  { key: 'utskrift', label: T('Utskrift §10-7'), onSelect: () => go('/arbeidstid?vis=utskrift') },
];

export function LonnHead({ tab, action }: { tab: 'grunnlag' | 'kost' | 'deltid'; action: ReactNode }) {
  const can = useCan();
  const { workspace } = useTenant();
  const go = useNavigate();
  const kost = can('vaktplan.kost') && workspace.available.scheduling && workspace.enabled.scheduling;
  const items: TabItem[] = [
    ...(can('timerloenn.se') || tab === 'grunnlag'
      ? [{ key: 'grunnlag', label: T('Lønnsgrunnlag'), to: '/lonn' }]
      : []),
    ...(kost || tab === 'kost' ? [{ key: 'kost', label: T('Lønnsprosent'), to: '/omsetning' }] : []),
    // part-timers against their agreed position, aml § 14-4 a (#109)
    ...(can('timerloenn.se') || tab === 'deltid'
      ? [{ key: 'deltid', label: T('Deltid'), to: '/deltid' }]
      : []),
  ];
  return (
    <PageHead
      title={T('Rapporter')}
      sub={T('Godkjente registreringer, lønnsarter og månedens eksport.')}
      actions={
        <>
          {can('timeliste.se') && (
            <Menu
              items={worktimeViews(go)}
              trigger={
                <button type="button" className="btn" data-testid="lonn-worktime">
                  <Icon name="clock" />
                  {T('Arbeidstid')}
                </button>
              }
            />
          )}
          {action}
        </>
      }
    >
      {items.length > 1 && <Tabs items={items} value={tab} label={T('Rapporter')} />}
    </PageHead>
  );
}
